import { effect, inject, Injectable } from "@angular/core";
import {
    applicationStringFromApplicationType,
    IBaseCard,
    isIFirmTaxprepT1Card,
    isJobsBilling,
    isJobsBillingCard,
    productStringFromApplicationType,
} from "@app/views-interfaces";
import { AnalyticsTrackData } from "src/app/views-interfaces/analytics.interface";
import { AppSettingsService } from "../app-settings-service/app-settings.service";
import { AuthService } from "../auth/auth.service";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { FirmsService } from "../firms-service/firms.service";
import { IframeMessagesService } from "../iframe-messages-service/iframe-messages.service";
import { AppVersionService } from "./../app-version-service/app-version.service";

type Mixpanel = typeof import("mixpanel-browser").default;

@Injectable({
    providedIn: "root",
})
export class AnalyticsService {
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly authService = inject(AuthService);
    private readonly appVersionService = inject(AppVersionService);
    private readonly firmsService = inject(FirmsService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly dashboardsService = inject(DashboardsService);

    private isMixpanelInitialized = false;
    private isMixpanelIdentified = false;

    private mixpanel: Mixpanel | null = null;
    private mixpanelQueue: Promise<void> = Promise.resolve();

    constructor() {
        // Settings and currentUser both resolve asynchronously; react to the signals instead of
        // awaiting promises so the constructor stays synchronous.
        effect(() => {
            const key = this.appSettingsService.getSetting("Mixpanel_Key");
            const currentUser = this.authService.currentUser();

            if (key && !this.isMixpanelInitialized) {
                this.isMixpanelInitialized = true;
                this.withMixpanel((mixpanel) => mixpanel.init(key, { track_pageview: true, persistence: "localStorage" }));
            }
            if (currentUser && this.isMixpanelInitialized && !this.isMixpanelIdentified) {
                this.isMixpanelIdentified = true;
                this.withMixpanel((mixpanel) => {
                    mixpanel.identify(currentUser.sub);
                    mixpanel.people.set({ firmId: currentUser.firm_id });
                });
            }
        });
    }

    /**
     * mixpanel-browser is a large dependency, so it is imported on demand to keep it out of the
     * initial bundle. Work is queued behind a single import so callers stay ordered: `init` always
     * runs before `identify`, which always runs before any `track`.
     */
    private withMixpanel(work: (mixpanel: Mixpanel) => void): void {
        this.mixpanelQueue = this.mixpanelQueue
            .then(async () => {
                this.mixpanel ??= (await import("mixpanel-browser")).default;
                work(this.mixpanel);
            })
            .catch((e: unknown) => console.error("Unable to send analytics.", e));
    }

    track(title: string, extra?: Record<string, unknown>): void {
        this.appVersionService.getVersion().subscribe(async (version: string) => {
            try {
                const currentDashboard = await this.dashboardsService.getCurrentDashboard();
                const currentUser = await this.authService.getCurrentUser();
                let data: AnalyticsTrackData = {
                    appVersion: version,
                    firmId: (await this.firmsService.getFirm())?.Id ?? "",
                    firmName: (await this.firmsService.getFirm())?.Name ?? "",
                    ctx_siteUrl: this.iframeMessagesService.messagesOrigin() ?? "",
                    ctx_application: applicationStringFromApplicationType(currentDashboard?.ApplicationType),
                    ctx_product: productStringFromApplicationType(currentDashboard?.ApplicationType),
                    ...(!isJobsBilling(currentDashboard?.ApplicationType) && { ctx_year: currentDashboard?.TaxYear }),
                    dashboardId: currentDashboard?.Id,
                    ifirm_session_name: currentUser?.ifirm_session_name ?? this.authService.sessionData()?.sessionName,
                    ifirm_userid: currentUser?.ifirm_userid,
                };
                if (extra) {
                    data = Object.keys(extra).reduce((acc: AnalyticsTrackData, key: string) => {
                        acc[key] = extra[key];
                        return acc;
                    }, data);
                }
                if (this.isMixpanelIdentified) this.withMixpanel((mixpanel) => mixpanel.track(title, data));
            } catch (e: unknown) {
                console.error("Unable to track analytics.", e);
            }
        });
    }
}

export function getAnalyticsExtrasFromCard(card: IBaseCard | null, extra: Record<string, unknown> = {}) {
    if (card) {
        extra.cardId = card.cardId;
        if (isJobsBillingCard(card)) {
            extra.ctx_documentId = card.return.documentId;
            extra.ctx_taxpayerId = card.return.returnId;
            extra.jobId = card.job.id;
        } else if (isIFirmTaxprepT1Card(card)) {
            extra.ctx_documentId = card.documentId;
            extra.ctx_taxpayerId = card.return.id;
        } else {
            extra.ctx_taxpayerId = card.return.id;
        }
    }
    return extra;
}
